/**
 * Privacy Policy — DRAFT (FR-P-3).
 *
 * Sprint 0 Day 2 ships placeholder copy per the Sprint Day-by-Day cut
 * point ("ToS / privacy policy can be a placeholder Day 2; final copy by
 * Day 6"). Final copy goes through Clinician Assist legal before public
 * launch (Sprint 6).
 *
 * Scope per Spec FR-P-3:
 * - HIPAA-adjacent posture
 * - GDPR / CCPA rights (incl. RTBF process per FR-R)
 * - Data retention windows
 * - Third-party processors (WorkOS, Stripe[MVP], Resend, Cloudflare,
 *   HeyGen, Sentry, PostHog, Honeycomb, Neon)
 */
import type * as React from "react";

export function PrivacyPolicy(): React.JSX.Element {
  return (
    <article className="prose mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
      <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
        DRAFT — placeholder copy, pending legal review
      </p>
      <h1 className="mt-2 text-3xl font-bold text-foreground">Privacy Policy</h1>
      <p className="mt-2 text-sm text-muted-foreground">Last updated: 2026-05-07</p>

      <section className="mt-8 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">Who we are</h2>
        <p>
          CaiT Academy is operated by Clinician Assist Inc. We provide
          educational training and credentialing for licensed clinicians on
          the Cognitive AI Therapy framework.
        </p>

        <h2 className="text-xl font-semibold text-foreground">
          What information we collect
        </h2>
        <p>
          We collect information you give us when you enroll (email, name,
          self-attested professional information) and information generated
          by your use of the course (lesson progress, knowledge-check
          attempts, exam attempts, capstone submission, reflection journal
          entries).
        </p>

        <h2 className="text-xl font-semibold text-foreground">
          How we use cookies
        </h2>
        <p>
          We use first-party cookies in three categories. <strong>Necessary</strong>{" "}
          cookies (your session, CSRF protection) load unconditionally so the
          site works. <strong>Analytics</strong> cookies (PostHog and Sentry
          session replay, with personally-identifying fields redacted) load
          only after you grant consent. <strong>Marketing</strong> cookies
          are reserved for future use; none are loaded in Phase 0.
        </p>

        <h2 className="text-xl font-semibold text-foreground">
          HIPAA-adjacent posture
        </h2>
        <p>
          The data we hold is education-grade, not clinical care. We treat the
          system as HIPAA-covered for design purposes. Capstone submissions
          may contain de-identified case material; we encrypt these fields
          using Fernet AES-128-CBC at rest. Identity is provided by WorkOS
          under a Business Associate Agreement.
        </p>

        <h2 className="text-xl font-semibold text-foreground">
          GDPR / CCPA rights and right to be forgotten
        </h2>
        <p>
          You can request export or deletion of your data via your account
          settings or by emailing privacy@cait.academy. Confirmed
          right-to-be-forgotten requests anonymize your record within 5
          business days. Certificate records are preserved with anonymized
          name to satisfy accreditor record-retention requirements; the
          credential ID remains valid for verification.
        </p>

        <h2 className="text-xl font-semibold text-foreground">
          Data retention
        </h2>
        <p>
          Credential-relevant audit events (enrollment, payment, exam,
          capstone, certificate) are retained for at least 7 years per
          accreditor guidance. Other audit events are retained for 2 years.
          Reflection journal entries are deleted on RTBF request.
        </p>

        <h2 className="text-xl font-semibold text-foreground">
          Third-party processors
        </h2>
        <ul className="list-disc pl-6">
          <li>WorkOS — identity (BAA in place)</li>
          <li>Resend — transactional email</li>
          <li>Cloudflare — content delivery, R2 object storage, Stream video</li>
          <li>HeyGen — module-intro video rendering</li>
          <li>Sentry — error monitoring (with PII redaction)</li>
          <li>PostHog — product analytics (PII-free events)</li>
          <li>Honeycomb — distributed tracing</li>
          <li>Neon — PostgreSQL database</li>
          <li>Fly.io — backend hosting</li>
        </ul>

        <h2 className="text-xl font-semibold text-foreground">
          Contact
        </h2>
        <p>
          Privacy questions: privacy@cait.academy
          <br />
          Data subject requests: privacy@cait.academy
        </p>
      </section>
    </article>
  );
}
